/* =============================================================================
   VIEWS / ADMIN / LOTS-ADMIN — Chargen / MHD + FEFO (Phase E6a + E6b, S165)
   =============================================================================
   Subtab „Chargen" der Gruppe „Ware". Aktive Chargen (stock_lots, Migration phase_124)
   sortiert nach MHD, zweistufiger MHD-Alarm (abgelaufen / läuft bald ab, Vorlauf
   konfigurierbar) und Ausbuchen abgelaufener Ware (write_off_lot → Schwund über den
   E1-Ledger). Chargen entstehen beim Wareneingang mit MHD (Lager / gegen Bestellung).

   E6b: Charge-Rest sinkt jetzt echt — Verkäufe (FEFO, früheste MHD zuerst) und manuelle
   Abgänge ziehen von den Chargen ab; „Details" zeigt die verbrauchenden Bewegungen/Orders
   (Rückverfolgbarkeit). Non-destruktive Renders; S161-Live-View-Registry; ohne window.func().
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { formatStock, movementLabel } from '../../core/stock.js';
import { lotAlarmLevel, lotColor, countByAlarm, formatCentsEuro, sumLotValueCents, countUnvaluedLots } from '../../core/lots.js';
import { registerLiveView } from '../../core/live-view-registry.js';

let _vorlauf = 3;     // mhdVorlaufTage (Settings)
let _lots = [];       // lot_overview()
let _byId = {};

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }

let _lotsLiveRegistered = false;
function ensureLotsLiveRegistered() {
  if (_lotsLiveRegistered) return;
  _lotsLiveRegistered = true;
  registerLiveView({ tabId: 'tab-lots', render: renderLotsAdmin, events: ['order:realtime'] });
}

export async function renderLotsAdmin() {
  ensureLotsLiveRegistered();
  const el = document.getElementById('tab-lots');
  if (!el) return;
  if (!flagOn()) {
    el.innerHTML = `<div class="card"><p class="hint">Chargen sind deaktiviert (Feature-Flag <code>ingredientsDb</code>).</p></div>`;
    return;
  }
  el.innerHTML = `<div class="card"><p class="hint">Lade Chargen…</p></div>`;
  try {
    const st = await storage().settings.get();
    const v = st && st.mhdVorlaufTage;
    _vorlauf = (v != null && isFinite(Number(v)) && Number(v) >= 0) ? Number(v) : 3;
  } catch (_) { _vorlauf = 3; }
  try {
    _lots = await storage().lots.overview(_vorlauf);
  } catch (e) {
    console.warn('[lots] overview:', e);
    _lots = [];
  }
  _byId = Object.fromEntries(_lots.map(l => [l.id, l]));
  el.innerHTML = renderHtml();
}

function renderHtml() {
  const today = new Date();
  const { expired, near } = countByAlarm(_lots, today, _vorlauf);
  const expBanner = expired ? `
    <div style="margin-top:12px; padding:10px 14px; border-radius:8px; background:var(--danger-soft,#fbe3e3); border:1px solid var(--danger,#b94a48);">
      <strong style="color:var(--danger,#b94a48);">⛔ ${expired} Charge(n) abgelaufen — ausbuchen.</strong>
    </div>` : '';
  const nearBanner = near ? `
    <div style="margin-top:12px; padding:10px 14px; border-radius:8px; background:var(--warn-soft,#fff4e0); border:1px solid var(--warn,#d9a227);">
      <strong style="color:var(--warn);">⚠️ ${near} Charge(n) laufen in ≤ ${_vorlauf} Tag(en) ab.</strong>
    </div>` : '';
  const rows = _lots.map(l => renderRowHtml(l, today)).join('');
  // S203 Cut B — Chargen-Lagerwert (Σ value_cents, chargen-genau); „nicht bewertet"-Zähler.
  const totalCents = sumLotValueCents(_lots);
  const unvalued = countUnvaluedLots(_lots);
  const valueLine = _lots.length
    ? `<span class="hint" style="font-size:.82rem;">Chargen-Wert gesamt: <strong>${formatCentsEuro(totalCents)}</strong>${unvalued > 0 ? ` · ${unvalued} Charge(n) ohne EK` : ''}</span>`
    : '';
  return `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:flex-end; gap:12px; flex-wrap:wrap;">
        <div>
          <h2 style="margin:0;">Chargen / MHD</h2>
          <p class="hint" style="margin:4px 0 0;">Aktive Chargen aus Wareneingängen mit MHD, sortiert nach MHD. Verkäufe ziehen FEFO (früheste MHD zuerst) von den Chargen ab; „Details" zeigt die Rückverfolgbarkeit. ${_lots.length} aktive Charge(n). ${valueLine}</p>
        </div>
        <div class="field" style="margin:0;"><label style="font-size:.78rem;">Vorlauf-Tage (Alarm)</label>
          <input id="lot-vorlauf" type="number" min="0" step="1" value="${_vorlauf}" style="width:80px;" onchange="lotSetVorlauf(this.value)"></div>
      </div>
      ${expBanner}
      ${nearBanner}
      <div style="margin-top:14px; overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:.88rem;">
          <thead><tr style="text-align:left; color:var(--ink-soft,#666);">
            <th style="padding:6px 8px;">Zutat</th>
            <th style="padding:6px 8px; text-align:right;">Eingang / Rest</th>
            <th style="padding:6px 8px; text-align:right;">EK / Wert</th>
            <th style="padding:6px 8px;">MHD</th>
            <th style="padding:6px 8px;">Lieferant</th>
            <th style="padding:6px 8px;">Eingang</th>
            <th style="padding:6px 8px;"></th>
          </tr></thead>
          <tbody>${rows || '<tr><td colspan="7" class="hint" style="padding:16px; text-align:center;">Keine aktiven Chargen. Beim Wareneingang ein MHD erfassen.</td></tr>'}</tbody>
        </table>
      </div>
    </div>`;
}

function renderRowHtml(l, today) {
  const level = lotAlarmLevel(l.mhd, today, _vorlauf);
  const color = lotColor(level);
  const mhdLabel = l.mhd
    ? `<span style="color:${color}; font-weight:${level === 'expired' || level === 'near' ? '700' : '400'};">${htmlEscape(l.mhd)}</span>${level === 'expired' ? ' <span class="hint" style="color:var(--danger,#b94a48); font-size:.74rem;">abgelaufen</span>' : (level === 'near' ? ` <span class="hint" style="color:var(--warn); font-size:.74rem;">in ${l.daysToExpiry} Tag(en)</span>` : '')}`
    : '<span class="hint">— kein MHD</span>';
  const received = l.receivedAt ? new Date(l.receivedAt).toLocaleDateString('de-DE') : '';
  // S203 Cut B — EK/Wert: Charge-Wert (Rest × EK) primär, EK je Basiseinheit (ct) als Hinweis.
  const baseLabel = l.baseUnit === 'piece' ? 'Stk' : (l.baseUnit || '');
  const ekHint = (l.unitCostCents != null && isFinite(Number(l.unitCostCents)))
    ? `<div class="hint" style="font-size:.72rem;">${(Math.round(Number(l.unitCostCents) * 100) / 100).toLocaleString('de-DE')} ct/${htmlEscape(baseLabel)}</div>`
    : '';
  const valueCell = l.valueCents != null
    ? `<strong>${formatCentsEuro(l.valueCents)}</strong>${ekHint}`
    : '<span class="hint">—</span>';
  return `
    <tr style="border-top:1px solid var(--border,#eee);">
      <td style="padding:6px 8px; font-weight:600;">${htmlEscape(l.ingredientName)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">${formatStock(l.qtyBase, l.baseUnit)} / <strong>${formatStock(l.remainingBase, l.baseUnit)}</strong></td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">${valueCell}</td>
      <td style="padding:6px 8px; white-space:nowrap;">${mhdLabel}</td>
      <td style="padding:6px 8px;" class="hint">${htmlEscape(l.supplierName || '—')}</td>
      <td style="padding:6px 8px; white-space:nowrap;" class="hint">${htmlEscape(received)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">
        <button class="btn btn-ghost btn-sm" onclick="lotOpenDetail('${htmlEscape(l.id)}')" title="Verbrauchende Bewegungen/Orders (FEFO)">Details</button>
        <button class="btn btn-ghost btn-sm" style="color:var(--danger,#b94a48);" onclick="lotWriteOff('${htmlEscape(l.id)}')" title="Restbestand als Schwund ausbuchen">Ausbuchen</button>
      </td>
    </tr>`;
}

export async function lotSetVorlauf(value) {
  const n = Number(value);
  if (!isFinite(n) || n < 0) { showToast('Vorlauf muss ≥ 0 sein', 'error'); return; }
  _vorlauf = Math.round(n);
  try {
    await storage().settings.update({ mhdVorlaufTage: _vorlauf });
  } catch (e) { console.warn('[lots] save vorlauf:', e); }
  // Alarm-Schwelle ändert sich → neu laden (RPC-Flags + Liste relativ zum Vorlauf)
  await renderLotsAdmin();
}

export async function lotWriteOff(lotId) {
  const l = _byId[lotId];
  if (!l) return;
  if (!(await window.styledConfirm({
    title: `Charge „${l.ingredientName}" als Schwund ausbuchen?`,
    body: `Rest ${formatStock(l.remainingBase, l.baseUnit)}${l.mhd ? ', MHD ' + l.mhd : ''}. Der Lager-Bestand sinkt und die Menge erscheint im Schwund-Report.`,
    danger: true, confirmLabel: 'Ausbuchen'
  })).confirmed) return;
  try {
    await storage().lots.writeOff(lotId);
    showToast('Charge ausgebucht — Lager & Schwund aktualisiert', 'success');
    await renderLotsAdmin();
  } catch (e) {
    console.error('[lots] writeOff:', e);
    showToast('Ausbuchen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// E6b — Rückverfolgbarkeit: welche Abgänge/Verkäufe zogen aus dieser Charge.
export async function lotOpenDetail(lotId) {
  const l = _byId[lotId];
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  const title = l ? htmlEscape(l.ingredientName) + (l.mhd ? ' · MHD ' + htmlEscape(l.mhd) : '') : 'Charge';
  content.innerHTML = `<div class="modal-header"><h2>Charge · ${title}</h2><button class="modal-close" onclick="closeModal()">×</button></div><p class="hint">Lade…</p>`;
  overlay.classList.add('active');
  let rows = [];
  try { rows = await storage().lots.allocationsFor(lotId); } catch (_) { rows = []; }
  const unit = l ? l.baseUnit : '';
  const body = rows.map(a => {
    const when = a.occurredAt ? new Date(a.occurredAt).toLocaleString('de-DE') : '';
    const ref = a.orderId ? ` <span class="hint" style="font-size:.72rem;">#${htmlEscape(String(a.orderId).slice(-6))}</span>` : '';
    return `
      <tr style="border-top:1px solid var(--border,#eee);">
        <td style="padding:5px 8px;">${htmlEscape(movementLabel(a.movementType, a.source))}${ref}</td>
        <td style="padding:5px 8px; text-align:right;">${formatStock(a.qtyBase, unit)}</td>
        <td style="padding:5px 8px;" class="hint" style="white-space:nowrap;">${htmlEscape(when)}</td>
      </tr>`;
  }).join('');
  content.innerHTML = `
    <div class="modal-header"><h2>Charge · ${title}</h2><button class="modal-close" onclick="closeModal()">×</button></div>
    ${l ? `<p class="hint" style="margin:0 0 8px;">Eingang ${formatStock(l.qtyBase, unit)} · Rest <strong>${formatStock(l.remainingBase, unit)}</strong>${l.supplierName ? ' · ' + htmlEscape(l.supplierName) : ''}</p>` : ''}
    <div style="max-height:50vh; overflow:auto;">
      <table style="width:100%; border-collapse:collapse; font-size:.86rem;">
        <thead><tr style="text-align:left; color:var(--ink-soft,#666);">
          <th style="padding:5px 8px;">Abgang</th><th style="padding:5px 8px; text-align:right;">Menge</th><th style="padding:5px 8px;">Zeit</th>
        </tr></thead>
        <tbody>${body || '<tr><td colspan="3" class="hint" style="padding:14px; text-align:center;">Noch keine Abgänge aus dieser Charge (FEFO greift bei Verkäufen/Schwund).</td></tr>'}</tbody>
      </table>
    </div>
    <div class="btn-row" style="margin-top:12px;"><button class="btn btn-ghost" onclick="closeModal()">Schließen</button></div>`;
}

if (typeof window !== 'undefined') {
  Object.assign(window, { renderLotsAdmin, lotSetVorlauf, lotWriteOff, lotOpenDetail });
}
